import axios from 'axios';
import { message } from 'antd';

export const API_BASE_URL = import.meta.env.PROD
  ? 'https://api.rtycn.com'
  : '/api';

export const BASE_URL = import.meta.env.PROD
  ? 'https://www.rtycn.com'
  : 'http://localhost:3000';
  
export const IMG_URL = import.meta.env.PROD
  ? 'https://img.rtycn.com'
  : '/uploads';
// UEditor图片资源路径前缀（用于nginx独立目录配置）
// 如果nginx配置了独立的静态资源目录，请修改此路径
export const UEDITOR_IMAGE_URL_PREFIX = import.meta.env.PROD
  ? 'https://img.rtycn.com/ueditor' // 生产环境图片资源域名
  : '/uploads/ueditor'; // 开发环境图片路径
// 创建axios实例
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加认证token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 添加用户信息到请求头（用于评论等功能）
    const user = JSON.parse(localStorage.getItem('current_user') || 'null');
    if (user) {
      config.headers['X-User-Id'] = user.id || '';
      config.headers['X-User-Username'] = user.username || '';
      config.headers['X-User-Avatar'] = user.avatar || '';
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 统一错误处理
api.interceptors.response.use(
  (response) => {
    if (response.data && response.data.success === false) {
      message.error(response.data.message || '操作失败，请稍后重试');
      return Promise.reject(new Error(response.data.message));
    }
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      message.error('登录已过期，请重新登录');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('current_user');
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    } else if (error.response?.status === 403) {
      message.error('没有权限执行此操作');
    } else if (error.response?.status === 404) {
      message.error('请求的资源不存在，请检查URL是否正确');
    } else if (error.response?.status === 413) {
      message.error('文件过大，请选择较小的文件上传');
    } else if (error.response?.status === 429) {
      message.error('请求过于频繁，请稍后再试');
    } else if (error.response?.status === 500) {
      message.error('服务器内部错误，请联系管理员或稍后重试');
    } else if (error.response?.status === 503) {
      message.error('服务暂时不可用，请稍后再试');
    } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      message.error('请求超时，请检查网络连接后重试');
    } else if (error.message === 'Network Error' || !error.response) {
      message.error('网络连接失败，请检查网络设置后重试');
    } else if (error.response?.data?.message) {
      message.error(error.response.data.message);
    } else {
      message.error('发生未知错误，请稍后重试');
    }
    return Promise.reject(error);
  }
);

// 认证相关API
export const authAPI = {
  // 获取当前用户信息
  getCurrentUser: () => api.get('/iden/auth/me'),

  // 注册用户
  register: (userData) => api.post('/iden/auth/register', userData),

  // 用户登录
  login: (credentials) => api.post('/iden/auth/login', credentials),

  // 刷新token
  refreshToken: () => api.post('/iden/auth/refresh'),

  // 发送重置密码邮件
  resetPassword: (data) => api.post('/iden/auth/reset-password', data),

  // 使用令牌确认重置密码
  confirmResetPassword: (data) => api.post('/iden/auth/reset-password/confirm', data),

  // 微信登录 - 生成二维码
  generateWechatQRCode: () => api.get('/iden/auth/wechat/qrcode'),

  // 微信登录 - 检查扫码状态
  checkWechatLoginStatus: (qrId) => api.get(`/iden/auth/wechat/check/${qrId}`),

  // 微信登录 - 绑定账号
  bindWechatAccount: (data) => api.post('/iden/auth/wechat/bind', data),

  // 微信登录 - 注册并绑定
  registerWithWechat: (data) => api.post('/iden/auth/wechat/register', data),

// 微信登录 - 测试接口：模拟扫码
  testScanWechat: (qrId, data) => api.post(`/iden/auth/wechat/test/scan/${qrId}`, data),
};

// 音乐系统（yinyue 模块）接口：独立域名 y.rtycn.com/iden/（nginx 反代到 yinyue 独立服务）
// 用于 app 注册用户时同步向音乐系统添加用户；失败不应影响主站注册流程
export const MUSIC_API_BASE_URL = import.meta.env.PROD
  ? 'https://y.rtycn.com/iden'
  : 'http://localhost:8961/iden';

// 音乐接口鉴权 key（需与 yinyue/config.json 的 api_key 保持一致）
const MUSIC_API_KEY = 'yinyue-rtycn-2026';

// 音乐接口使用独立 axios 实例（不走默认拦截器，避免 baseURL 拼错路径）
export const musicUserAPI = {
  // 添加音乐用户；返回音乐 envelope：{ code, msg, data }（code=0 成功）
  add: (userData) => axios.post(`${MUSIC_API_BASE_URL}/user/add`, {
    username: userData.username,
    password: userData.password,
    nickname: userData.nickname || '',
  }, {
    headers: { 'Content-Type': 'application/json', 'X-Yinyue-Key': MUSIC_API_KEY },
    timeout: 5000,
  }),

  // 用户名查重
  checkExists: (username) => axios.get(`${MUSIC_API_BASE_URL}/user/exists`, {
    params: { username },
    headers: { 'X-Yinyue-Key': MUSIC_API_KEY },
    timeout: 5000,
  }),
};

// 用户管理API
export const userAPI = {

  // 获取所有用户
  getAllUsers: () => api.get('/iden/users/all'),

  // 获取用户详情
  getUser: (userId) => api.get(`/iden/users/show/${userId}`),

  // 更新用户
  updateUser: (userId, userData) => api.put(`/iden/users/edit/${userId}`, userData),

  // 删除用户
  deleteUser: (userId) => api.delete(`/iden/users/delete/${userId}`),

  // 修改密码
  changePassword: (username, newPassword) =>
    api.post(`/iden/users/${username}/change-password`, { newPassword }),

  // 重置密码
  resetPassword: (email) => api.post('/iden/auth/reset-password', { email }),

  // 修改邮箱
  updateEmail: (username, newEmail) =>
    api.post(`/iden/users/${username}/change-email`, { newEmail }),

  // 修改手机号
  updatePhone: (username, newPhone) =>
    api.post(`/iden/users/${username}/change-phone`, { newPhone }),

  // 修改昵称
  updateNickname: (username, newNickname) =>
    api.post(`/iden/users/${username}/change-nickname`, { newNickname }),
};

// 相册管理API
export const albumAPI = {
  // 创建相册
  createAlbum: (albumData) => api.post('/album/albums/create', albumData),

  // 获取相册列表
  getAlbums: (params) => api.get('/album/albums', { params }),

  // 获取相册详情
  getAlbum: (albumId) => api.get(`/album/albums/show/${albumId}`),

  // 更新相册
  updateAlbum: (albumId, albumData) => api.put(`/album/albums/edit/${albumId}`, albumData),

  // 删除相册
  deleteAlbum: (albumId) => api.delete(`/album/albums/delete/${albumId}`),

  // 获取最新相册
  getLatestAlbums: (limit = 10) => api.get(`/album/albums/latest?limit=${limit}`),

  // 获取指定作者的最新相册
  getLatestAlbumsByAuthor: (authorId, limit = 10) => api.get(`/album/albums/author/${authorId}/latest?limit=${limit}`),

  // 获取热门相册
  getHotAlbums: (limit = 10) => api.get(`/album/albums/hot?limit=${limit}`),

  // 按标签获取相册
  getAlbumsByTag: (tag) => api.get(`/album/albums/tag/${tag}`),

  // 按作者获取相册
  getAlbumsByAuthor: (authorId) => api.get(`/album/albums/author/${authorId}`),

  // 获取指定作者的指定相册
  getAlbumByAuthorAndId: (authorId, albumId) => api.get(`/album/albums/author/${authorId}/album/${albumId}`),

  // 点赞相册
  likeAlbum: (albumId) => api.post(`/album/albums/${albumId}/like`),
};

// 照片管理API
export const photoAPI = {
  // 上传照片
  uploadPhoto: (formData) => api.post('/album/photos/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // 获取照片详情
  getPhoto: (photoId) => api.get(`/album/photos/show/${photoId}`),

  // 更新照片
  updatePhoto: (photoId, photoData) => api.put(`/album/photos/edit/${photoId}`, photoData),

  // 删除照片
  deletePhoto: (photoId) => api.delete(`/album/photos/delete/${photoId}`),

  // 获取相册照片
  getPhotosByAlbum: (albumId) => api.get(`/album/photos/album/${albumId}`),

  // 点赞照片
  likePhoto: (photoId) => api.post(`/album/photos/${photoId}/like`),

  // 获取允许的文件类型
  getAllowedFileTypes: () => api.get('/album/photos/allowed-types'),
};

// 博客管理API
export const blogAPI = {
  // 创建博客
  createBlog: (blogData) => api.post('/blog/blogs/create', blogData),

  // 获取博客列表
  getBlogs: (params) => api.get('/blog/blogs', { params }),

  // 获取博客详情
  getBlog: (blogId) => api.get(`/blog/blogs/show/${blogId}`),

  // 更新博客
  updateBlog: (blogId, blogData) => api.put(`/blog/blogs/edit/${blogId}`, blogData),

  // 发布博客
  publishBlog: (blogId) => api.put(`/blog/blogs/${blogId}/publish`),

  // 删除博客
  deleteBlog: (blogId) => api.delete(`/blog/blogs/delete/${blogId}`),

  // 获取最新博客
  getLatestBlogs: (limit = 10) => api.get(`/blog/blogs/latest?limit=${limit}`),

  // 获取指定作者的最新博客
  getLatestBlogsByAuthor: (authorId, limit = 10) => api.get(`/blog/blogs/author/${authorId}/latest?limit=${limit}`),

  // 获取热门博客
  getHotBlogs: (limit = 10) => api.get(`/blog/blogs/hot?limit=${limit}`),

  // 按分类获取博客
  getBlogsByCategory: (category) => api.get(`/blog/blogs/category/${category}`),

  // 按标签获取博客
  getBlogsByTag: (tag) => api.get(`/blog/blogs/tag/${tag}`),

  // 按作者获取博客
  getBlogsByAuthor: (authorId) => api.get(`/blog/blogs/author/blog/${authorId}`),

  // 点赞博客
  likeBlog: (blogId) => api.post(`/blog/blogs/${blogId}/like`),
};

// 评论管理API
export const commentAPI = {
  // 添加评论
  addComment: (commentData) => api.post('/blog/comment/add', commentData),

  // 获取博客评论
  getCommentsByBlog: (blogId, page = 1, size = 10) =>
    api.get(`/blog/comment/blog/${blogId}?page=${page}&size=${size}`),

  // 获取评论详情
  getComment: (commentId) => api.get(`/blog/comment/${commentId}`),

  // 更新评论
  updateComment: (commentId, commentData) => api.put(`/blog/comment/edit/${commentId}`, commentData),

  // 删除评论
  deleteComment: (commentId) => api.delete(`/blog/comment/delete/${commentId}`),

  // 获取评论数
  getCommentCount: (blogId) => api.get(`/blog/comment/count/blog/${blogId}`),
};

// 分类管理API
export const categoryAPI = {
  // 创建分类
  createCategory: (categoryData) => api.post('/blog/categories/create', categoryData),

  // 获取分类列表
  getCategories: () => api.get('/blog/categories/all'),

  // 获取分类详情
  getCategory: (categoryId) => api.get(`/blog/categories/show/${categoryId}`),

  // 更新分类
  updateCategory: (categoryId, categoryData) => api.put(`/blog/categories/edit/${categoryId}`, categoryData),

  // 删除分类
  deleteCategory: (categoryId) => api.delete(`/blog/categories/delete/${categoryId}`),

  // 禁用分类
  disableCategory: (categoryId) => api.put(`/blog/categories/${categoryId}/disable`),

  // 按父级获取分类
  getCategoriesByParent: (parentId) => api.get(`/blog/categories/parent/${parentId}`),

  // 按作者获取分类
  getCategoriesByAuthor: (authorId) => api.get(`/blog/categories/author/${authorId}`),

  // 搜索分类
  searchCategories: (name) => api.get(`/blog/categories/search?name=${name}`),

  // 获取分类统计
  getCategoryCount: () => api.get('/blog/categories/count'),
};

// 用户资料API
export const profileAPI = {
  // 获取用户资料
  getProfile: (username) => api.get(`/iden/profiles/user/${username}`),

  // 更新用户资料
  updateProfile: (username, profileData) => api.put(`/iden/profiles/user/${username}`, profileData),

  // 上传头像
  uploadAvatar: (username, formData) => api.post(`/iden/profiles/user/${username}/avatar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // 更新自定义样式
  updateCustomStyle: (username, style) => api.put(`/iden/profiles/user/${username}/custom-style`, { style }),

  // 更新样式选择
  updateStyle: (username, styleId) => api.put(`/iden/profiles/user/${username}/style`, { styleId }),

  // 获取允许的头像类型
  getAllowedAvatarTypes: () => api.get('/iden/profiles/allowed-avatar-types'),
};

// 博客样式API
export const styleAPI = {
  // 创建样式
  createStyle: (styleData) => api.post('/iden/styles', styleData),

  // 获取样式列表
  getStyles: () => api.get('/iden/styles'),

  // 获取样式详情
  getStyle: (styleId) => api.get(`/iden/styles/${styleId}`),

  // 更新样式
  updateStyle: (styleId, styleData) => api.put(`/iden/styles/${styleId}`, styleData),

  // 删除样式
  deleteStyle: (styleId) => api.delete(`/iden/styles/${styleId}`),

  // 禁用样式
  disableStyle: (styleId) => api.put(`/iden/styles/${styleId}/disable`),

  // 获取默认样式
  getDefaultStyles: () => api.get('/iden/styles/default'),

  // 按作者获取样式
  getStylesByAuthor: (authorId) => api.get(`/iden/styles/author/${authorId}`),

  // 搜索样式
  searchStyles: (name) => api.get(`/iden/styles/search?name=${name}`),

  // 上传预览图
  uploadPreviewImage: (styleId, formData) => api.post(`/iden/styles/${styleId}/preview`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // 获取允许的预览类型
  getAllowedPreviewTypes: () => api.get('/iden/styles/allowed-preview-types'),
};

// 留言API
export const messageAPI = {
  // 发送留言
  sendMessage: (messageData) => api.post('/iden/message/send', messageData),

  // 获取接收到的留言
  getReceivedMessages: (userId, page = 1, pageSize = 10) =>
    api.get(`/iden/message/received/${userId}?page=${page}&pageSize=${pageSize}`),

  // 获取发送的留言
  getSentMessages: (userId, page = 1, pageSize = 10) =>
    api.get(`/iden/message/sent/${userId}?page=${page}&pageSize=${pageSize}`),

  // 标记为已读
  markAsRead: (messageId) => api.put(`/iden/message/read/${messageId}`),

  // 删除留言
  deleteMessage: (messageId) => api.delete(`/iden/message/delete/${messageId}`),
};

// 关注API
export const followAPI = {
  // 关注用户
  follow: (followerId, followingId) => api.post('/iden/follow', { followerId, followingId }),

  // 取消关注
  unfollow: (followerId, followingId) => api.delete('/iden/follow', { data: { followerId, followingId } }),

  // 检查是否关注
  checkFollow: (followerId, followingId) =>
    api.get(`/iden/follow/check?followerId=${followerId}&followingId=${followingId}`),

  // 获取关注的用户列表
  getFollowing: (userId, page = 1, pageSize = 10) =>
    api.get(`/iden/follow/following/${userId}?page=${page}&pageSize=${pageSize}`),

  // 获取粉丝列表
  getFollowers: (userId, page = 1, pageSize = 10) =>
    api.get(`/iden/follow/followers/${userId}?page=${page}&pageSize=${pageSize}`),
};

// 博客评论API
export const blogCommentAPI = {
  // 添加评论
  addComment: (commentData) => api.post('/blog/comment/add', commentData),

  // 获取博客的评论列表
  getCommentsByBlog: (blogId, page = 1, pageSize = 10) =>
    api.get(`/blog/comment/blog/${blogId}?page=${page}&pageSize=${pageSize}`),

  // 更新评论
  updateComment: (commentId, content) => api.put(`/blog/comment/edit/${commentId}`, { content }),

  // 删除评论
  deleteComment: (commentId) => api.delete(`/blog/comment/delete/${commentId}`),
};

// 照片评论API
export const photoCommentAPI = {
  // 添加评论
  addComment: (commentData) => api.post('/album/comment/add', commentData),

  // 获取照片的评论列表
  getCommentsByPhoto: (photoId, page = 1, pageSize = 10) =>
    api.get(`/album/comment/photo/${photoId}?page=${page}&pageSize=${pageSize}`),

  // 更新评论
  updateComment: (commentId, content) => api.put(`/album/comment/edit/${commentId}`, { content }),

  // 删除评论
  deleteComment: (commentId) => api.delete(`/album/comment/delete/${commentId}`),
};

export default api;